import type { MessageSuccessResponse } from '@/types/auth'
import type { FeedbackPayload } from '@/types/feedback'
import type { FormError } from '@/types/httpError'
import { computed, defineComponent, reactive, ref } from 'vue'
import { feedbackApiCall } from '@/api/feedback'
import { useApiCall } from '@/composables/useApiCall'
import { validateEmail } from '@/helpers/validateEmail'
import { useAuthStore } from '@/store/auth'

export default defineComponent({
  name: 'FeedbackPopup',
  props: {
    isOpen: { type: Boolean, required: false },
  },
  emits: ['popupClose'],
  setup(_, { emit }) {
    const authStore = useAuthStore()
    const isSend = ref(false)
    const errors = ref<null | FormError['data']>(null)
    const fields = reactive({
      fullName: authStore.user ? authStore.user.data.fullName : '',
      email: authStore.user ? authStore.user.email : '',
      phone: authStore.user ? authStore.user.data.phone : '',
      message: '',
    })

    const formFields = reactive({
      fullName: {
        placeholder: 'ФИО',
        type: 'text',
      },
      email: {
        placeholder: 'E-mail',
        type: 'email',
      },
      phone: {
        placeholder: '+7 *** *** ** **',
        type: 'text',
      },
      message: {
        placeholder: 'Ваш вопрос',
        type: 'text',
      },
    })

    const checkFieldValidity = (key: string, value: string | null) => {
      switch (key) {
        case 'phone':
          return (value as string).replaceAll(' ', '').length > 11
        case 'email':
          return validateEmail(value as string)
        default:
          return !!value
      }
    }

    const isFormValid = computed(() => {
      const fieldsValues = Object.entries(fields)
      return fieldsValues.every(entries => checkFieldValidity(...entries))
    })

    const closePopup = () => {
      emit('popupClose')
    }

    const {
      isLoading: isFeedbackLoading,
      data: feedbackResponse,
      error: feedbackError,
      executeApiCall: feedbackAction,
    } = useApiCall<MessageSuccessResponse, FormError, FeedbackPayload>(
      feedbackApiCall,
      true,
    )

    const checkError = (key: string) => {
      if (errors.value && errors.value[key]) {
        delete errors.value[key]
      }
    }

    const sendForm = async () => {
      errors.value = null
      try {
        await feedbackAction(fields)
        if (feedbackResponse.value) {
          isSend.value = true
        }
      }
      catch {
        if (feedbackError.value) {
          errors.value = feedbackError.value.data.data
        }
      }
    }

    return {
      fields,
      isSend,
      formFields,
      closePopup,
      checkFieldValidity,
      isFormValid,
      sendForm,
      isFeedbackLoading,
      errors,
      checkError,
    }
  },
})
